import React, { useState, useEffect } from "react"
import { motion } from "framer-motion"
import {Save, X, FileText, DollarSign, Calendar, Hash, User} from 'lucide-react'
import { useGuias } from "../context/GuiasContext"

interface CadastrarBoletoProps {
  editingBoletoId?: string
  onSaveComplete?: () => void
}

export default function CadastrarBoleto({ editingBoletoId, onSaveComplete }: CadastrarBoletoProps) {
  const { addBoleto, updateBoleto, getBoletoById, dadosPrefeitura } = useGuias()
  
  const [formData, setFormData] = useState({
    nomePagador: "",
    cpfCnpjPagador: "",
    enderecoPagador: "",
    descricao: "",
    valor: "",
    dataVencimento: "",
    dataEmissao: new Date().toISOString().split("T")[0],
    linhaDigitavel: "",
    nossoNumero: "",
    convenio: "",
    carteira: "",
    variacaoCarteira: "",
    agencia: "128-7",
    conta: "7798-4",
    numeroDocumento: "",
    juros: "",
    multa: "",
    desconto: "",
    instrucoes: "",
    observacoes: ""
  })

  useEffect(() => {
    if (editingBoletoId) {
      const boleto = getBoletoById(editingBoletoId)
      if (boleto) {
        setFormData({
          nomePagador: boleto.nomePagador || "",
          cpfCnpjPagador: boleto.cpfCnpjPagador || "",
          enderecoPagador: boleto.enderecoPagador || "",
          descricao: boleto.descricao || "",
          valor: boleto.valor || "",
          dataVencimento: boleto.dataVencimento?.split("T")[0] || "",
          dataEmissao: boleto.dataEmissao?.split("T")[0] || "",
          linhaDigitavel: boleto.linhaDigitavel || "",
          nossoNumero: boleto.nossoNumero || "",
          convenio: boleto.convenio || "",
          carteira: boleto.carteira || "",
          variacaoCarteira: boleto.variacaoCarteira || "",
          agencia: boleto.agencia || "128-7",
          conta: boleto.conta || "7798-4",
          numeroDocumento: boleto.numeroDocumento || "",
          juros: boleto.juros || "",
          multa: boleto.multa || "",
          desconto: boleto.desconto || "",
          instrucoes: boleto.instrucoes || "",
          observacoes: boleto.observacoes || ""
        })
      }
    }
  }, [editingBoletoId, getBoletoById])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const formatCPFCNPJ = (value: string) => {
    const numbers = value.replace(/\D/g, "")
    if (numbers.length <= 11) {
      return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4")
    } else {
      return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5")
    }
  }

  const handleCPFCNPJChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCPFCNPJ(e.target.value)
    setFormData(prev => ({ ...prev, cpfCnpjPagador: formatted }))
  }

  const handleValorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/[^\d,]/g, "")
    value = value.replace(",", ".")
    setFormData(prev => ({ ...prev, valor: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.nomePagador || !formData.cpfCnpjPagador || !formData.valor || !formData.dataVencimento) {
      alert("Preencha todos os campos obrigatórios!")
      return
    }

    const boletoData = {
      ...formData,
      dataVencimento: new Date(formData.dataVencimento + "T00:00:00").toISOString(),
      dataEmissao: new Date(formData.dataEmissao + "T00:00:00").toISOString()
    }

    if (editingBoletoId) {
      await updateBoleto(editingBoletoId, boletoData)
    } else {
      await addBoleto(boletoData)
    }

    handleReset()
    if (onSaveComplete) onSaveComplete()
  }

  const handleReset = () => {
    setFormData({
      nomePagador: "",
      cpfCnpjPagador: "",
      enderecoPagador: "",
      descricao: "",
      valor: "",
      dataVencimento: "",
      dataEmissao: new Date().toISOString().split("T")[0],
      linhaDigitavel: "",
      nossoNumero: "",
      convenio: "",
      carteira: "",
      variacaoCarteira: "",
      agencia: "128-7",
      conta: "7798-4",
      numeroDocumento: "",
      juros: "",
      multa: "",
      desconto: "",
      instrucoes: "",
      observacoes: ""
    })
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-lg p-8"
    >
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <FileText className="w-6 h-6 text-[#004161]" />
          {editingBoletoId ? "Editar Boleto" : "Cadastrar Novo Boleto"}
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Preencha as informações para gerar um boleto bancário
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Dados do Pagador */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <User className="w-5 h-5 text-[#004161]" />
            Dados do Pagador
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome Completo *
              </label>
              <input
                type="text"
                name="nomePagador"
                value={formData.nomePagador}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                CPF/CNPJ *
              </label>
              <input
                type="text"
                name="cpfCnpjPagador"
                value={formData.cpfCnpjPagador}
                onChange={handleCPFCNPJChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
                required
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Endereço Completo
              </label>
              <input
                type="text"
                name="enderecoPagador"
                value={formData.enderecoPagador}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Dados do Boleto */}
        <div className="bg-green-50 p-4 rounded-lg">
          <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-[#004161]" />
            Dados do Boleto
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descrição/Serviço
              </label>
              <input
                type="text"
                name="descricao"
                value={formData.descricao}
                onChange={handleChange}
                placeholder="Ex: Taxa de Sepultamento, Aforamento, etc."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Valor (R$) *
              </label>
              <input
                type="text"
                name="valor"
                value={formData.valor}
                onChange={handleValorChange}
                placeholder="0.00"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data de Vencimento *
              </label>
              <input
                type="date"
                name="dataVencimento"
                value={formData.dataVencimento}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data de Emissão
              </label>
              <input
                type="date"
                name="dataEmissao"
                value={formData.dataEmissao}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Dados Bancários */}
        <div className="bg-purple-50 p-4 rounded-lg">
          <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Hash className="w-5 h-5 text-[#004161]" />
            Dados Bancários (Integração Futura)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Agência
              </label>
              <input
                type="text"
                name="agencia"
                value={formData.agencia}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent bg-gray-100"
                readOnly
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Conta Corrente
              </label>
              <input
                type="text"
                name="conta"
                value={formData.conta}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent bg-gray-100"
                readOnly
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Convênio
              </label>
              <input
                type="text"
                name="convenio"
                value={formData.convenio}
                onChange={handleChange}
                placeholder="Aguardando API"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Carteira
              </label>
              <input
                type="text"
                name="carteira"
                value={formData.carteira}
                onChange={handleChange}
                placeholder="Aguardando API"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nosso Número
              </label>
              <input
                type="text"
                name="nossoNumero"
                value={formData.nossoNumero}
                onChange={handleChange}
                placeholder="Gerado pela API"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Linha Digitável
              </label>
              <input
                type="text"
                name="linhaDigitavel"
                value={formData.linhaDigitavel}
                onChange={handleChange}
                placeholder="Gerada pela API"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Juros, Multa e Desconto */}
        <div className="bg-amber-50 p-4 rounded-lg">
          <h3 className="font-semibold text-gray-700 mb-3">
            Juros, Multa e Desconto
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Juros (% ao mês)
              </label>
              <input
                type="text"
                name="juros"
                value={formData.juros}
                onChange={handleChange}
                placeholder="0.00"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Multa (%)
              </label>
              <input
                type="text"
                name="multa"
                value={formData.multa}
                onChange={handleChange}
                placeholder="0.00"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Desconto (R$)
              </label>
              <input
                type="text"
                name="desconto"
                value={formData.desconto}
                onChange={handleChange}
                placeholder="0.00"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Instruções e Observações */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Instruções para o Caixa
            </label>
            <textarea
              name="instrucoes"
              value={formData.instrucoes}
              onChange={handleChange}
              rows={3}
              placeholder="Ex: Não receber após o vencimento"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Observações
            </label>
            <textarea
              name="observacoes"
              value={formData.observacoes}
              onChange={handleChange}
              rows={3}
              placeholder="Observações adicionais"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
            />
          </div>
        </div>

        {/* Botões de Ação */}
        <div className="flex gap-4 pt-4">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="submit"
            className="flex-1 bg-gradient-to-r from-[#004161] to-[#005580] text-white py-3 px-6 rounded-lg font-semibold flex items-center justify-center gap-2 hover:shadow-lg transition-shadow"
          >
            <Save className="w-5 h-5" />
            {editingBoletoId ? "Atualizar Boleto" : "Cadastrar Boleto"}
          </motion.button>
          
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="button"
            onClick={handleReset}
            className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold flex items-center gap-2 hover:bg-gray-50 transition-colors"
          >
            <X className="w-5 h-5" />
            Limpar
          </motion.button>
        </div>
      </form>

      {/* Aviso sobre API */}
      <div className="mt-6 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
        <p className="text-sm text-yellow-700">
          <strong>Aguardando integração com API do Banco do Brasil:</strong> Os campos de Nosso Número, Linha Digitável e Código de Barras serão preenchidos automaticamente quando a API estiver configurada.
        </p>
      </div>
    </motion.div>
  )
}
